/* eslint-disable no-param-reassign, max-lines, max-params */
import { api } from "jiffy-api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import initCable from "components/pod/pod-build-order/dtf-channel";
import {
  trackImageUploadCompleted,
  trackImageProcessingCompleted,
  trackUploadError
} from "components/pod/pod-build-order/utils/tracker";
import { getPageSourceParam } from "components/jiffy/product-details/customizable-made-page-utils";
import { getImageInfo, fileToBlob } from "../utils/preload-image";

const ARTWORK_STATUSES = {
  initial: "initial",
  upload: "upload",
  validate: "validate",
  process: "process",
  complete: "complete"
};

const PROGRESS_RATE = 0.1;
const PROGRESS_CATCHUP_RATE = 1.1;
const PROGRESS_INTERVAL = 100;

const artworkStatusTexts = {
  initial: {
    title: "Upload your design",
    message: "Do not forget to upload your design",
    percentage: {}
  },
  upload: {
    title: "Uploading your artwork...",
    message: "Less than 3 minutes left",
    percentage: { start: 0, end: 35 }
  },
  validate: {
    title: "Validating your artwork...",
    message: "Less than 2 minutes left",
    percentage: { start: 35, end: 50 }
  },
  process: {
    title: "Processing your artwork...",
    message: "Less than 1 minute left",
    percentage: { start: 50, end: 95 }
  },
  complete: {
    title: "",
    message: "Few seconds left",
    percentage: { start: 100, end: 100 }
  }
};

const initialState = {
  touched: false,
  comment: "",
  enhanced: false,
  uploadAbortSignal: null,
  dtfChannel: null,
  dtfChannelUuid: "",
  artwork: null,
  uploadedImage: null,
  artworkSize: {
    width: 0,
    height: 0
  },
  uploadArtwork: false,
  uploadErrorText: "",
  uploadStatus: ARTWORK_STATUSES.initial,
  uploadProgress: 0.0,
  actionCableConnected: true,
  lastWebSocketEvent: null,
  lastWebSocketEventData: {},
  editType: null,
  side: null,
  presetLoaded: false
};

const artworkModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    removeBackground: (_, getters) => getters.artwork?.remove_background,
    artworkUrl: (state, { artwork, removeBackground, enhanced }) => {
      if (!artwork) return URL.createObjectURL(state.uploadedImage || new Blob());
      const {
        preloaded,
        original_image_url: originalBg,
        no_bg_url: noBg,
        with_bg_url: withBg,
        url: originalUrl
      } = artwork;
      if (preloaded) return originalUrl;
      if (!enhanced) return originalBg;
      if (removeBackground) return noBg || withBg;
      return withBg;
    },
    comment: state => state.comment,
    uploadErrorText: state => state.uploadErrorText,
    enhanced: state => state.enhanced,
    touched: state => state.touched,
    artworkSize: state => state.artworkSize,
    artwork: state => state.artwork,
    uploadArtwork: state => state.uploadArtwork,
    artworkExists: (_, getters) => !!getters.artwork || getters.uploadArtwork,
    uploadStatus: state => artworkStatusTexts[state.uploadStatus],
    uploadProgress: state => state.uploadProgress,
    serializedArtwork: (_, getters) => {
      if (!getters.artwork) return null;
      return {
        dtf_artwork: {
          id: getters.artwork.id,
          enhance: getters.enhanced,
          remove_background: Boolean(getters.removeBackground),
          comment: getters.comment
        },
        width: getters.artworkSize.width,
        height: getters.artworkSize.height,
        position: ""
      };
    },
    isConnectionSetup: state => Boolean(state.dtfChannelUuid),
    editType: state => state.editType,
    presetLoaded: state => state.presetLoaded
  },

  mutations: {
    setRemoveBackground(state, isRemoved) {
      state.artwork.remove_background = isRemoved;
    },
    setArtwork(state, artwork) {
      state.artwork = artwork;
    },
    clearArtworkSize(state) {
      state.artworkSize = { width: null, height: null };
    },
    setArtworkTouched(state, touched) {
      state.touched = touched;
    },
    setUploadStatus(state, status) {
      if (!ARTWORK_STATUSES[status]) return;
      state.uploadStatus = ARTWORK_STATUSES[status];
    },
    setEditType(state, value) {
      state.editType = value;
    },
    setSide(state, side) {
      state.side = side;
    }
  },

  actions: {
    /* eslint-disable max-lines-per-function */
    async upload({ state, commit, dispatch }, { originalFile, suppressEvents = false }) {
      let uploadStartTime;
      let isImageCorrect = false;
      let file = originalFile;

      try {
        file = await fileToBlob(originalFile);
        await getImageInfo(URL.createObjectURL(file));
        isImageCorrect = true;
      } catch (error) {
        dispatch("handleUploadError", { error: new Error("Invalid image"), file, uploadStartTime });
      }

      if (!isImageCorrect) return;

      try {
        state.uploadErrorText = "";
        state.uploadArtwork = true;

        commit("setUploadStatus", "upload");
        dispatch("setArtworkEnhanced", true);
        commit("setArtworkTouched", true);
        dispatch("incrementUploadArtworkProgress");
        state.uploadedImage = file;
        state.uploadAbortSignal = new AbortController();

        const formData = new FormData();
        const processTimeStamp = Date.now();

        formData.append("file", file);
        formData.append("product_id", 432292);
        formData.append("file_original_index", 0);
        formData.append("dtf_channel_uuid", state.dtfChannelUuid);
        formData.append("pre_cut", false);
        formData.append("process_time_stamp", processTimeStamp);

        uploadStartTime = performance.now();

        const artwork = await api.dtf.uploadArtwork(formData, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined },
          signal: state.uploadAbortSignal.signal
        });
        commit("setArtwork", { ...artwork, preloaded: true });
        dispatch("saveArtworkToPreset", { artwork });

        if (!suppressEvents) {
          trackImageUploadCompleted({
            file,
            uploadStartTime,
            artwork,
            source: getPageSourceParam()
          });
        }
      } catch (error) {
        dispatch("handleUploadError", { error, file, uploadStartTime });
      }
    },
    // eslint-disable-next-line max-lines-per-function
    async createDtfChannel({ state, commit, dispatch }) {
      const response = await api.dtf.getInitConfig({ catalog_number: "DTF001" });
      const { success, error, dtfChannelUuid } = objNamesToCamelCase(response);

      if (!success) throw new Error(error); // attempt to retrieve error from server if present

      state.dtfChannelUuid = dtfChannelUuid;

      state.dtfChannel = initCable(dtfChannelUuid, {
        received(data) {
          const { result, action } = data;
          const { artwork } = result;
          if (
            result.error &&
            ["upload_artwork", "process_artwork", "validate_artwork"].includes(action)
          ) {
            dispatch("handleProcessingError", { artwork, result });
          }

          if (action === "upload_artwork") {
            this.perform("process_dtf_artwork", data.data);
            commit("setUploadStatus", "validate");
          }

          if (action === "validate_artwork") {
            commit("setUploadStatus", "process");
          }

          if (action === "process_artwork") {
            commit("setArtwork", result.process_data);
            commit("setUploadStatus", "complete");
            state.uploadArtwork = false;
            trackImageProcessingCompleted({
              result,
              dtfChannelUuid,
              action,
              source: getPageSourceParam()
            });
          }

          state.lastWebSocketEvent = data.action;
          state.lastWebSocketEventData = {
            result: data.result?.process_data || data.data,
            data: data.result?.data || data.data
          };
        },
        connected() {
          const { lastWebSocketEvent, lastWebSocketEventData, actionCableConnected } = state;
          if (actionCableConnected) return;

          if (lastWebSocketEvent && Object.keys(lastWebSocketEventData).length > 0) {
            // Reset artwork processTimeStamp
            // There will be cases where discontinued continue sending messages.
            // Rejecting it to prioritize messages coming from this process since
            // this is the source of truth.
            const processTimeStamp = Date.now();
            this.perform("continue_dtf_artwork_process", {
              last_process: lastWebSocketEvent,
              last_process_data: lastWebSocketEventData.result,
              data: { ...lastWebSocketEventData.data, processTimeStamp }
            });

            state.lastWebSocketEvent = null;
            state.lastWebSocketEventData = {};
          }
        },
        disconnected() {
          state.actionCableConnected = false;
        }
      });
    },
    handleUploadError({ getters, state }, { error, file, uploadStartTime }) {
      state.uploadArtwork = false;

      if (error.name === "AbortError" || error.name === "CanceledError") return;

      const errorResponse = error?.response?.data || error;
      state.uploadErrorText = errorResponse?.error || error.message;

      const handledError = ["backend_file_validation", "backend_processing_error"].includes(
        errorResponse?.error_type
      );

      if (
        (errorResponse.error && !errorResponse.success) ||
        (navigator.onLine && error.message !== "Network Error")
      ) {
        if (handledError) {
          trackImageUploadCompleted({
            file,
            response: errorResponse,
            uploadStartTime,
            artwork: getters.artwork,
            source: getPageSourceParam()
          });
        }
        trackUploadError({ file, errorResponse, source: getPageSourceParam() });
      }
    },
    handleProcessingError({ state }, { artwork, result }) {
      state.uploadArtwork = false;
      state.uploadedImage = null;

      if (result.error && !result.success) {
        trackUploadError({
          file: {
            name: artwork?.name,
            type: artwork?.filetype,
            size: artwork?.filesize,
            width: artwork?.metadata?.width,
            height: artwork?.metadata?.height
          },
          errorResponse: result,
          source: getPageSourceParam()
        });
      }
    },
    incrementUploadArtworkProgress({ state, dispatch }) {
      if (!state.uploadArtwork || state.uploadProgress >= 100) {
        state.uploadArtwork = false;
        return;
      }

      const { start: lowerLimit, end: upperLimit } =
        artworkStatusTexts[state.uploadStatus].percentage;

      if (state.uploadProgress < upperLimit) {
        const progressRate =
          state.uploadProgress < lowerLimit ? PROGRESS_CATCHUP_RATE : PROGRESS_RATE;
        const remaining = parseFloat((100 - state.uploadProgress).toFixed(2));

        state.uploadProgress = parseFloat(
          (state.uploadProgress + Math.min(progressRate, remaining)).toFixed(2)
        );
      }

      setTimeout(() => {
        dispatch("incrementUploadArtworkProgress");
      }, PROGRESS_INTERVAL);
    },
    async removeArtwork({ state, dispatch }) {
      state.uploadAbortSignal?.abort();
      state.dtfChannel?.unsubscribe();
      state.presetLoaded = false;
      Object.assign(state, { ...initialState, side: state.side, touched: state.touched });
      dispatch(`preset_${state.side}/clearPresetQueue`, null, { root: true });
      try {
        await api.printOnDemand.removeMadePreset({
          side: state.side
        });
      } catch (e) {
        console.warn("No preset to remove");
      }
      await dispatch("createDtfChannel");
    },
    setArtworkEnhanced({ state, dispatch }, enhanced) {
      state.enhanced = enhanced;
      dispatch("sendQueryToPreset", {
        enhance_image: enhanced
      });
    },
    setArtworkComment({ state, dispatch }, comment) {
      state.comment = comment;
      dispatch("sendQueryToPreset", {
        enhancement_request_note: comment
      });
    },
    setArtworkSize({ state, dispatch, rootGetters }, size) {
      state.artworkSize = size;
      const { width, height } = size;
      const inchWidth = rootGetters[`surface_${state.side}/convertPxToInches`](width);
      const inchHeight = rootGetters[`surface_${state.side}/convertPxToInches`](height);
      dispatch("sendQueryToPreset", {
        width: inchWidth,
        height: inchHeight
      });
    },
    sendQueryToPreset({ state, dispatch }, query) {
      dispatch(
        `preset_${state.side}/addToPresetQueue`,
        {
          ...query
        },
        { root: true }
      );
    },
    saveArtworkToPreset({ state, rootGetters, dispatch }, { artwork }) {
      const { width, height } = state.artworkSize;
      const inchWidth = rootGetters[`surface_${state.side}/convertPxToInches`](width);
      const inchHeight = rootGetters[`surface_${state.side}/convertPxToInches`](height);

      dispatch(
        `preset_${state.side}/createPreset`,
        {
          position: rootGetters[`surface_${state.side}/surfaceLabel`],
          dtf_artwork_id: artwork.id,
          enhance_image: state.enhanced,
          width: inchWidth,
          height: inchHeight
        },
        { root: true }
      );
    },
    setDataFromPreset({ state, rootGetters }, preset) {
      const { width, height } = preset;
      const pxWidth = rootGetters[`surface_${state.side}/convertInchesToPx`](width);
      const pxHeight = rootGetters[`surface_${state.side}/convertInchesToPx`](height);
      state.presetLoaded = true;
      if (state.uploadStatus === ARTWORK_STATUSES.initial) {
        state.uploadStatus = ARTWORK_STATUSES.complete;
        state.artwork = preset.dtf_artwork;
      }
      state.enhanced = preset.enhance_image;
      state.comment = preset.enhancement_request_note;

      state.artworkSize = {
        width: pxWidth,
        height: pxHeight
      };
    }
  }
};

export default artworkModule;
